import React, { useState, useEffect } from 'react';
import PreprocessModule from './components/PreprocessModule';
import DockingModule from './components/DockingModule';
import ADMETPredictionForm from './components/ADMETPredictionForm';
import DynamicsSimulationModule from './components/DynamicsSimulation';
import ReinventTransferModule from './components/ReinventTransferModule';
import QSARModule from './components/QSARModule';
import CrystalPredictionModule from './components/CrystalPredictionModule';
import PharmacophoreScreeningModule from './components/PharmacophoreScreeningModule';
import LPSPredictionForm from './components/LPSPredictionForm';
import AntibodyStructurePredictionModule from './components/AntibodyStructurePredictionModule';
import AntibodyComplexPredictionModule from './components/AntibodyComplexPredictionModule';
import AntibodyAffinityPredictionModule from './components/AntibodyAffinityPredictionModule';
import TaskHistory from './components/TaskHistory';
import ModuleCardWrapper from './components/ModuleCardWrapper';
import useTaskManager from './hooks/useTaskManager';
import './App.css';

function App() {
  const {
    history,
    setHistory,
    taskStatuses,
    setTaskStatuses,
    handleTask,
    checkStatus,
    clearHistory
  } = useTaskManager();

  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const savedTheme = localStorage.getItem('dark_mode');
    if (savedTheme === 'true') {
      setDarkMode(true);
      document.body.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('dark_mode', newMode.toString());
    document.body.classList.toggle('dark', newMode);
  };

  const filteredHistory = history.filter(item => {
    const keyword = searchTerm.toLowerCase();
    return (
      item.type?.toLowerCase().includes(keyword) ||
      item.method?.toLowerCase().includes(keyword) ||
      item.time?.toLowerCase().includes(keyword)
    );
  });

  return (
    <div className="app-container">
      {/* 顶部栏 */}
      <div className="header-bar">
        <h1 className="text-xl font-bold">🌐 AIDRUG.WORK</h1>
        <button onClick={toggleDarkMode} className="toggle-button">
          {darkMode ? '🌞 浅色模式' : '🌙 深色模式'}
        </button>
      </div>

      {/* 模块区域 */}
      <div className="grid-container w-full max-w-screen-xl mx-auto">
        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="⚙️ 蛋白预处理" description="上传结构文件并自动标准化蛋白质">
            <PreprocessModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🔍 结合位点预测" description="上传蛋白结构，预测潜在配体结合口袋">
            <LPSPredictionForm onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>
        
        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="⚛ 分子对接" description="选择方法并上传分子进行自动对接">
            <DockingModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🧪 ADMET预测" description="上传小分子文件，预测其ADMET属性">
            <ADMETPredictionForm onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🌊 动力学模拟" description="上传结构并选择模拟工具进行运行">
            <DynamicsSimulationModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🤖 分子生成" description="上传SMILES文件，微调模型并生成新分子">
            <ReinventTransferModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="📊 QSAR" description="上传化合物特征表进行建模预测">
            <QSARModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🧱 晶型预测" description="上传分子结构预测结晶构型">
            <CrystalPredictionModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🎯 药效团筛选" description="基于药效团模型筛选候选分子">
            <PharmacophoreScreeningModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>

        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🧬 抗体结构预测" description="上传重链和轻链序列/结构预测三维结构">
            <AntibodyStructurePredictionModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>
        
        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="🔗 抗原-抗体复合物预测" description="上传重链、轻链与抗原结构预测复合物">
            <AntibodyComplexPredictionModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>
        
        <div className="w-full max-w-[320px] mx-auto">
          <ModuleCardWrapper title="⚖️ 抗体亲和力预测" description="上传重链、轻链与抗原序列预测结合亲和力">
            <AntibodyAffinityPredictionModule onSubmit={handleTask} />
          </ModuleCardWrapper>
        </div>
      </div>

      {/* 任务历史记录 */}
      <div className="task-history-wrapper mt-6">
        <input
          type="text"
          placeholder="🔍 搜索任务类型 / 方法 / 时间"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 mb-4 border rounded-md dark:bg-gray-700 dark:text-white"
        />
        <TaskHistory
          history={filteredHistory}
          setHistory={setHistory}
          taskStatuses={taskStatuses}
          setTaskStatuses={setTaskStatuses}
          onCheckStatus={checkStatus}
          onClear={clearHistory}
        />
      </div>
    </div>
  );
}

export default App;

